import React, { useCallback, useRef, useState } from 'react';
import {
  NodeViewWrapper,
  Node,
  ReactNodeViewRenderer,
  mergeAttributes,
  type NodeViewProps,
} from '@tiptap/react';
import { Box, IconButton, Progress, Text, VStack } from '@chakra-ui/react';
import { Trash } from 'lucide-react';
import { assignPersonaId, createFileJSON, createMediaJSON } from './contentJSONs';

export type UploadResult = { url: string; fileDrn?: string };

export type UploadFunction = (
  file: File,
  onProgress?: (event: { progress: number }) => void,
  abortSignal?: AbortSignal,
) => Promise<UploadResult>;

interface ImageUploadNodeOptions {
  accept?: string;
  limit?: number;
  maxSize?: number;
  upload?: UploadFunction;
  onError?: (error: Error) => void;
  onSuccess?: (url: string) => void;
}

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    imageUpload: {
      setImageUploadNode: (options?: ImageUploadNodeOptions) => ReturnType;
    };
  }
}

interface FileItem {
  id: string;
  file: File;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  url?: string;
  abortController?: AbortController;
}

interface UploadOptions {
  maxSize: number;
  limit: number;
  accept: string;
  upload: UploadFunction;
  onSuccess?: (url: string) => void;
  onError?: (error: Error) => void;
}

interface ImageUploadPreviewProps {
  file: File;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  onRemove: () => void;
}

interface ImageUploadDragAreaProps {
  onFile: (files: File[]) => void;
  children: React.ReactNode;
}

function ImageUploadPreview({ file, progress, status, onRemove }: ImageUploadPreviewProps) {
  const formatFileSize = (bytes: number) => {
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  return (
    <Box mt={4} borderWidth="1px" borderRadius="md" p={4} position="relative">
      {status === 'uploading' && (
        <Progress size="xs" value={progress} mb={2} borderRadius="md" colorScheme="blue" />
      )}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Text fontWeight="medium" isTruncated whiteSpace="wrap">
            {file.name}
          </Text>
          <Text fontSize="sm" color="gray.500">
            {formatFileSize(file.size)}
          </Text>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {status === 'uploading' && <Text fontSize="sm">{progress}%</Text>}
          <IconButton
            size="sm"
            aria-label="Remove"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            icon={
              <Box as="span" fontWeight="bold">
                x
              </Box>
            }
            variant="ghost"
            colorScheme="gray"
          />
        </Box>
      </Box>
    </Box>
  );
}

function DropZoneContent({ maxSize }: { maxSize: number }) {
  return (
    <VStack spacing={1}>
      <Text fontSize="sm" fontWeight="medium">
        <Text as="span" textDecoration="underline">
          Click to upload
        </Text>{' '}
        or drag and drop
      </Text>
      <Text fontSize="xs" color="gray.500">
        Max size: {(maxSize / 1024 / 1024).toFixed(0)}MB
      </Text>
    </VStack>
  );
}

function ImageUploadDragArea({ onFile, children }: ImageUploadDragAreaProps) {
  const [dragOver, setDragOver] = useState(false);

  return (
    <Box
      p={6}
      border="2px dashed"
      borderColor={dragOver ? 'blue.400' : 'gray.300'}
      borderRadius="md"
      textAlign="center"
      cursor="pointer"
      background={dragOver ? 'blue.50' : 'transparent'}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        onFile(files);
      }}
    >
      {children}
    </Box>
  );
}

function useFileUpload(options: UploadOptions) {
  const [fileItems, setFileItems] = useState<FileItem[]>([]);

  const uploadFile = useCallback(
    async (file: File): Promise<UploadResult | null> => {
      if (file.size > options.maxSize) {
        options.onError?.(new Error(`File too large: ${file.name}`));
        return null;
      }

      const abortController = new AbortController();
      const id = crypto.randomUUID();

      const newItem: FileItem = {
        id,
        file,
        progress: 0,
        status: 'uploading',
        abortController,
      };

      setFileItems((prev) => [...prev, newItem]);

      try {
        const result = await options.upload(
          file,
          (event) => {
            setFileItems((prev) =>
              prev.map((item) => (item.id === id ? { ...item, progress: event.progress } : item)),
            );
          },
          abortController.signal,
        );

        if (!result?.url) {
          throw new Error('Upload failed');
        }

        setFileItems((prev) =>
          prev.map((item) =>
            item.id === id ? { ...item, status: 'success', progress: 100, url: result.url } : item,
          ),
        );

        options.onSuccess?.(result.url);
        return result;
      } catch (err) {
        if (!abortController.signal.aborted) {
          setFileItems((prev) =>
            prev.map((item) => (item.id === id ? { ...item, status: 'error', progress: 0 } : item)),
          );
          options.onError?.(err instanceof Error ? err : new Error('Upload failed'));
        }
        return null;
      }
    },
    [options],
  );

  const uploadFiles = useCallback(
    async (files: File[]): Promise<UploadResult[] | null> => {
      if (!files.length) {
        options.onError?.(new Error('No files provided'));
        return null;
      }

      if (options.limit && files.length > options.limit) {
        options.onError?.(
          new Error(`Max ${options.limit} file${options.limit === 1 ? '' : 's'} allowed`),
        );
        return null;
      }

      const results = await Promise.allSettled(files.map(uploadFile));

      const successfulUploads: (UploadResult | null)[] = [];
      const uploadErrors: Error[] = [];

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          successfulUploads.push(result.value);
        } else {
          uploadErrors.push(result.reason);
        }
      });

      if (uploadErrors.length > 0) {
        const combinedError = new Error(
          `${uploadErrors.length} out of ${files.length} files failed to upload.`,
        );
        options.onError?.(combinedError);
      }

      return successfulUploads.filter((r): r is UploadResult => r !== null);
    },
    [options, uploadFile],
  );

  const clearFileItem = useCallback((id?: string) => {
    setFileItems((prev) => {
      prev.forEach((item) => {
        if (!id || item.id === id) {
          item.abortController?.abort();
          if (item.url) {
            URL.revokeObjectURL(item.url);
          }
        }
      });
      return id ? prev.filter((item) => item.id !== id) : [];
    });
  }, []);

  return {
    fileItems,
    uploadFiles,
    clearFileItem,
  };
}

function ImageUploadNodeComponent(props: NodeViewProps) {
  const { accept, limit, maxSize } = props.node.attrs;
  const { deleteNode } = props;
  const extension = props.extension;
  const inputRef = useRef<HTMLInputElement>(null);

  const { fileItems, uploadFiles, clearFileItem } = useFileUpload({
    maxSize,
    limit,
    accept,
    upload: extension.options.upload,
    onSuccess: extension.options.onSuccess,
    onError: extension.options.onError,
  });

  const handleUpload = async (files: File[]) => {
    const results = await uploadFiles(files);
    if (!results) {
      return;
    }

    const pos = props.getPos();
    const nodes = files
      .map((file, i) => {
        const result = results[i];
        if (!result) {
          return null;
        }

        const name = file.name.replace(/\.[^/.]+$/, '') || 'untitled';
        const node = file.type.startsWith('image/')
          ? createMediaJSON(file, 'img')
          : file.type.startsWith('video/')
            ? createMediaJSON(file, 'video')
            : createFileJSON(file);

        node.attrs = {
          ...node.attrs,
          src: result.url,
          alt: name,
          title: name,
          fileDrn: result.fileDrn,
        };
        return assignPersonaId(node);
      })
      .filter(Boolean);

    if (nodes.length > 0) {
      props.editor
        .chain()
        .focus()
        .deleteRange({ from: pos, to: pos + 1 })
        .insertContentAt(pos, nodes)
        .run();
    }
  };

  const handleClick = () => {
    if (fileItems?.length === 0 && inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  return (
    <NodeViewWrapper
      as={Box}
      role="group"
      tabIndex={0}
      mt={4}
      p={2}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      position="relative"
      onClick={handleClick}
    >
      {!props.node.attrs.simulation && (
        <Box
          position="absolute"
          top={2}
          right={2}
          display="none"
          _groupHover={{ display: 'block' }}
        >
          <IconButton
            size="sm"
            icon={<Trash size={16} />}
            aria-label="Delete"
            onClick={(e) => {
              e.stopPropagation();
              deleteNode();
            }}
          />
        </Box>
      )}

      {fileItems.length === 0 && (
        <ImageUploadDragArea onFile={handleUpload}>
          <DropZoneContent maxSize={maxSize} />
        </ImageUploadDragArea>
      )}

      {fileItems.map((item) => (
        <ImageUploadPreview
          key={item.id}
          file={item.file}
          progress={item.progress}
          status={item.status}
          onRemove={() => clearFileItem(item.id)}
        />
      ))}

      <input
        ref={inputRef}
        name="file"
        accept={accept}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) {
            void handleUpload(Array.from(e.target.files));
          }
        }}
        onClick={(e) => e.stopPropagation()}
      />
    </NodeViewWrapper>
  );
}

export const FileUploadExtension = Node.create<ImageUploadNodeOptions>({
  name: 'upload-component',
  group: 'block',
  draggable: true,

  addOptions() {
    return {
      accept: '*/*',
      limit: 1,
      maxSize: 0,
      upload: undefined,
      onSuccess: undefined,
      onError: undefined,
    };
  },

  addAttributes() {
    return {
      accept: { default: this.options.accept },
      limit: { default: this.options.limit },
      maxSize: { default: this.options.maxSize },
    };
  },

  parseHTML() {
    return [{ tag: 'upload-component' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['upload-component', mergeAttributes(HTMLAttributes), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageUploadNodeComponent);
  },

  addCommands() {
    return {
      setImageUploadNode:
        (options = {}) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: options,
          }),
    };
  },
});
